'use client';

import * as Dialog from '@radix-ui/react-dialog';

import { buttons } from '@/styles/buttons';
import { dialogCloseBtnStyles } from '@/styles/dialog';
import { fontStyles } from '@/styles/typography';

import { ChevronRightIcon } from '@/ui/Icons/ChevronRightIcon';
import { MatchaGreenIcon } from '@/ui/Icons/MatchaGreenIcon';
import { WalletIcon } from '@/ui/Icons/WalletIcon';
import { XIcon } from '@/ui/Icons/XIcon';
import { Modal } from '@/ui/Modal';

import { classNames } from '@/utils/classnames';

/** Which option the user picked in the chooser. Mirrors the gated analytics enum. */
export type ChooserOption = 'matcha_account' | 'wallet';

type WalletConnectionChooserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Whether the Matcha-account option is offered (flag on). */
  showMatchaAccount: boolean;
  onSelect: (option: ChooserOption) => void;
};

const optionRow = classNames(
  buttons({ color: 'gray', shape: 'rounded', size: 'medium' }),
  'w-full',
  'justify-start',
  'gap-3',
  'px-4',
  'py-3.5',
  'h-auto',
);

function ChooserOptionButton({
  icon,
  title,
  description,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className={optionRow} onClick={onClick}>
      <span className="shrink-0 flex items-center justify-center w-8 h-8">{icon}</span>
      <span className="flex flex-col items-start grow text-left">
        <span className={fontStyles({ variant: 'LABEL_LG' })}>{title}</span>
        <span className={fontStyles({ variant: 'BODY_SM', class: ['text-token-gray-700'] })}>
          {description}
        </span>
      </span>
      <ChevronRightIcon className="shrink-0 text-token-gray-700" />
    </button>
  );
}

/**
 * Presentational chooser for the wallet-connection entrypoint. Stateless: the
 * owning {@link WalletConnectionModalProvider} controls visibility and routes
 * the selected option to Privy.
 */
export function WalletConnectionChooserModal({
  isOpen,
  onClose,
  showMatchaAccount,
  onSelect,
}: WalletConnectionChooserModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="MEDIUM">
      <div className="items-center flex justify-between px-4 pt-5.5">
        <Dialog.Title className={fontStyles({ variant: 'H5' })}>Connect</Dialog.Title>
        <Dialog.Close
          aria-label="Close"
          className={dialogCloseBtnStyles().button({ absolute: true })}
        >
          <XIcon aria-hidden className={dialogCloseBtnStyles().icon()} />
        </Dialog.Close>
      </div>
      <Dialog.Description
        className={fontStyles({ variant: 'BODY_SM', class: ['px-4', 'text-token-gray-700'] })}
      >
        Choose how you want to connect to Matcha.
      </Dialog.Description>
      <div className="flex flex-col gap-2 p-4">
        {showMatchaAccount && (
          <ChooserOptionButton
            icon={<MatchaGreenIcon width={28} height={28} />}
            title="Use Matcha account"
            description="Sign in to trade with your Matcha wallet"
            onClick={() => onSelect('matcha_account')}
          />
        )}
        <ChooserOptionButton
          icon={<WalletIcon width={24} height={24} className="text-token-gray-1600" />}
          title="Connect a wallet"
          description="Use an external wallet like MetaMask or Phantom"
          onClick={() => onSelect('wallet')}
        />
      </div>
    </Modal>
  );
}
